<script lang="ts">
    import RewardsCurrency from "@apps/rewards/components/RewardsCurrency.svelte";
    import RewardsPoints from "@apps/rewards/components/RewardsPoints.svelte";
    import RewardsVipTiersGoal from "@apps/rewards/components/RewardsVipTiers/RewardsVipTiersGoal/RewardsVipTiersGoal.svelte";
    import type RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
    import type RewardsCurrencyFormatter from "@apps/rewards/lib/RewardsCurrencyFormatter";
    import type { RewardsVipTierEntity } from "@apps/rewards/modules/VipTiers/entities/RewardsVipTiersEntities";
    import type { TranslationNamespaces } from "@apps/rewards/types";
    import RewardsVipTiersSingleProgressWidgetDynamicImport from "@apps/rewards/widgets/rewards_vip_tiers_single_progress_widget/RewardsVipTiersSingleProgressWidgetDynamicImport.svelte";
    import type { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import type { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import type HTMLSanitizerInterface from "@modules/money/HTMLSanitizer/HTMLSanitizerInterface";

    import T from "@components/T.svelte";
    import Text from "@components/Typography/Text.svelte";
    import type { LoggerInterface } from "@interfaces/LoggerInterface";

    //Props
    export let customerCurrentTier: string | undefined;
    export let customerNextTier: RewardsVipTierEntity | undefined;
    export let i18n: GwI18n;
    export let currencyFormatter: RewardsCurrencyFormatter;
    export let amountFormatter: RewardsAmountFormatter;
    export let dynamicImportManager: DynamicImportManager;
    export let logger: LoggerInterface;
    export let sanitizer: HTMLSanitizerInterface;
    export let namespace: TranslationNamespaces;
    export let pointsNamespace: TranslationNamespaces;
</script>

<div
    class="gw-rd-vip-tiers-page-widget-tier-progress"
    class:text-center={!customerNextTier}
>
    <div
        class="gw-rd-vip-tiers-page-widget-tier-progress__status"
        class:display-none={!customerNextTier}
    >
        {#if customerCurrentTier && customerNextTier}
            <Text component="span" variant="secondary" size="xs">
                <T
                    {namespace}
                    translationKey="vip_tiers_progress_status"
                    translationOptions={{ tier: customerCurrentTier }}
                />
            </Text>
        {/if}
        {#if customerNextTier}
            <Text component="span" variant="secondary" size="xs">
                <RewardsVipTiersGoal tierCondition={customerNextTier.condition}>
                    <RewardsPoints
                        slot="points-goal"
                        points={customerNextTier.conditionValue}
                        {amountFormatter}
                        {i18n}
                        {pointsNamespace}
                    >
                        <T
                            let:value={{ points, pointsName }}
                            slot="default"
                            {namespace}
                            translationKey="vip_tiers_progress_points_to_earn"
                            translationOptions={{
                                condition: points,
                                points_name: pointsName,
                                tier: customerNextTier.title,
                            }}
                        />
                    </RewardsPoints>
                    <T
                        slot="purchases-goal"
                        {namespace}
                        translationKey="vip_tiers_progress_make_purchases"
                        translationOptions={{
                            condition: customerNextTier.conditionValue,
                            tier: customerNextTier.title,
                        }}
                    />
                    <RewardsCurrency
                        slot="spent-amount-goal"
                        amount={customerNextTier.conditionValue}
                        {currencyFormatter}
                        {sanitizer}
                    >
                        <T
                            let:amount
                            slot="default"
                            {namespace}
                            translationKey="vip_tiers_progress_spend_amount"
                            translationOptions={{
                                condition: amount,
                                tier: customerNextTier.title,
                            }}
                        />
                    </RewardsCurrency>
                    <T
                        slot="referrals-goal"
                        {namespace}
                        translationKey="vip_tiers_progress_refer_friends"
                        translationOptions={{
                            condition: customerNextTier.conditionValue,
                            tier: customerNextTier.title,
                        }}
                    />
                </RewardsVipTiersGoal>
            </Text>
        {/if}
    </div>
    <RewardsVipTiersSingleProgressWidgetDynamicImport
        {dynamicImportManager}
        {logger}
    />
</div>

<style lang="postcss">
    .gw-rd-vip-tiers-page-widget-tier-progress {
        text-align: left;
        max-width: 670px;
        width: 100%;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--gw-rd-space-6);
        padding: clamp(
            var(--gw-rd-space-6),
            -2px + 3.2vw,
            var(--gw-rd-space-20)
        );
        margin: 0 auto;
        box-sizing: border-box;
        background-color: var(--gw-rd-light-skyblue-bg-color);
        border-radius: var(--gw-rd-border-radius-md);

        &.text-center {
            text-align: center;
        }

        &__status {
            display: flex;
            flex-direction: column;
            gap: var(--gw-rd-space-6);
            justify-content: space-between;
            align-items: start;
            flex: 1 0 max-content;

            &.display-none {
                display: none;
            }
        }

        :global(.gw-rd-vip-tiers-single-progress-widget-container) {
            flex: 1.5 0 max-content;
        }
    }
</style>
